<template>
  <sticky-header-layout>
    <template #header>
      <div class="filter-container">
        <div class="filter-item">
          <span class="filter-title">Report Type:</span>
          <gx-select
            v-model="report_type"
            :searchable="false"
            :disabled="isLoading"
            :allow-empty="true"
            :options="reportTypeOptions"
            track-by="value"
            label="label"
            @input="didUpdateReportType"
          >
            <template #selectedLabel="{ option }">
              {{ option.label }}
            </template>
            <template #option="{ option }">
              {{ option.label }}
            </template>
          </gx-select>
        </div>
      </div>
      <div class="filter-container">
        <div class="filter-item">
          <span class="filter-title">Order Status:</span>
          <gx-select
            v-model="filter.order_status"
            :searchable="false"
            :disabled="isLoading"
            :allow-empty="true"
            :options="orderStatusOptions"
            track-by="value"
            label="label"
          >
            <template #selectedLabel="{ option }">
              {{ option.label }}
            </template>
            <template #option="{ option }">
              {{ option.label }}
            </template>
          </gx-select>
        </div>

        <div class="filter-item">
          <span class="filter-title">Shopper:</span>
          <gx-select
            v-model="filter.shopper"
            :disabled="isLoading || report_type.value !== 'sales'"
            :allow-empty="true"
            :is-loading="isLoadingShopperList"
            :options="shopperOptions"
            track-by="id"
            label="name"
          >
            <template #selectedLabel="{ option }">
              {{ option.name }}
            </template>
            <template #option="{ option }">
              {{ option.name }}
            </template>
          </gx-select>
        </div>

        <div class="filter-item">
          <span class="filter-title">Merchant:</span>
          <gx-select
            v-model="filter.store"
            :disabled="isLoading"
            :allow-empty="true"
            :is-loading="isLoadingStoreList"
            :options="storeOptions"
            track-by="id"
            label="name"
          >
            <template #selectedLabel="{ option }">
              {{ option.name }}
            </template>
            <template #option="{ option }">
              {{ option.name }}
            </template>
          </gx-select>
        </div>

        <div class="filter-item">
          <span class="filter-title">Brand:</span>
          <el-input
            v-model="filter.brand_name"
            :disabled="isLoading || report_type.value !== 'sales'"
            clearable
          />
        </div>

        <div class="filter-item">
          <span class="filter-title">Category:</span>
          <gx-select
            v-model="filter.category"
            :searchable="false"
            :disabled="isLoading || report_type.value !== 'sales'"
            :allow-empty="true"
            :options="categoryOptions"
            track-by="value"
            label="label"
          >
            <template #selectedLabel="{ option }">
              {{ option.label }}
            </template>
            <template #option="{ option }">
              {{ option.label }}
            </template>
          </gx-select>
        </div>
        
        <div class="filter-item">
          <span class="filter-title">Date:</span>
          <gx-date-range-select
            :date-range.sync="filter.dateRange"
            :disabled="isLoading"
            @selectedDateRange="didSelectDateRange"
          />
        </div>

        <div class="filter-item">
          <el-button
            type="primary"
            icon="el-icon-search"
            size="mini"
            @click="handleSearch"
          >
            Search
          </el-button>
        </div>        
      </div>
    </template>

    <el-table
      v-if="report_type.value === 'sales'"
      v-loading="isLoading"
      :data="list"
      stripe
      border
      style="width: 100%"
    >
      <el-table-column
        prop="order_status"
        label="Order Status"
        width="80"
      />

      <el-table-column
        prop="order_date"
        label="Goxip Order Date"
        width="100"
      />

      <el-table-column
        prop="order_number"
        label="Goxip Order No."
        width="100"
      />

      <el-table-column
        prop="shopper_name"
        label="Shopper Name"
        width="100"
      />

      <el-table-column
        prop="customer_name"
        label="Customer Name"
        width="100"
      />

      <el-table-column
        prop="store_name"
        label="Merchant"
        width="100"
      />

      <el-table-column
        prop="brand_name"
        label="Brand"
        width="100"
      />

      <el-table-column
        prop="category"
        label="Category"
        width="100"
      />

      <el-table-column
        prop="quantity"
        label="Order Unit"
        width="100"
      />

      <el-table-column
        prop="gmv"
        label="GMV (excluded shipping)"
        width="100"
      />

      <el-table-column
        prop="merchant_order_price"
        label="Merchant Order Price"
        width="100"
      />

      <el-table-column
        prop="net_profit"
        label="Net Profit"
        width="100"
      />

      <el-table-column
        prop="shopper_price_difference_commission_percentage"
        label="Commission Rate (%)"
        width="100"
      />

      <el-table-column
        prop="shopper_price_difference_commission_hkd"
        label="Actual Earned (HKD)"
        width="100"
      />

      <el-table-column
        prop="cpa_percentage"
        label="CPA (%)"
        width="100"
      />

      <el-table-column
        prop="commission_hkd_to_goxip"
        label="Commission to Goxip (HKD)"
        width="100"
      />

      <el-table-column
        prop="shopper_cpa_commission_percentage"
        label="Commission Rate (%)"
        width="100"
      />

      <el-table-column
        prop="shopper_cpa_commission_hkd"
        label="Actual Earned (HKD)"
        width="100"
      />

      <el-table-column
        prop="shopper_total_commission_hkd"
        label="Total Commission (HKD)"
        width="100"
      />
    </el-table>

    <el-table
      v-if="report_type.value === 'merchant-sales'"
      v-loading="isLoading"
      :data="list"
      stripe
      border
      style="width: 100%"
    >
      <el-table-column
        prop="order_status"
        label="Order Status"
        width="120"
      />

      <el-table-column
        prop="order_date"
        label="Merchant Order Date"
        width="100"
      />

      <el-table-column
        prop="goxip_order_number"
        label="Goxip Order No."
        width="150"
      />

      <el-table-column
        prop="merchant_order_number"
        label="Merchant Order No."
        width="150"
      />

      <el-table-column
        prop="store_name"
        label="Merchant"
        width="100"
      />

      <el-table-column
        prop="quantity"
        label="Order Unit"
        width="100"
      />

      <el-table-column
        prop="merchant_order_price"
        label="Merchant Order Price"
        width="100"
      />

      <el-table-column
        prop="cpa_percentage"
        label="CPA (%)"
        width="100"
      />

      <el-table-column
        prop="commission_hkd_to_goxip"
        label="Commission to Goxip (HKD)"
        min-width="150"
      />
    </el-table>
  </sticky-header-layout>
</template>

<script>
import format from 'date-fns/format'
import PrivateClientsService from '../../api/private-clients'

export default {
  name: 'PrivateClientReport',
  data() {
    return {
      filter: this.getDefaultFilter(),
      reportTypeOptions: [
        { value: 'sales', label: 'Customer Sales Report' },
        { value: 'merchant-sales', label: 'Merchant Sales Report' },
      ],
      orderStatusOptions: [
        { value: 'sales', label: 'Sales' },
        { value: 'return', label: 'Return' },
      ],
      shopperOptions: [],
      storeOptions: [],
      categoryOptions: [
        { value: 'Top', label: 'Top' },
        { value: 'Bottom', label: 'Bottom' },
        { value: 'Shoes', label: 'Shoes' },
        { value: 'Bag', label: 'Bag' },
        { value: 'Watch', label: 'Watch' },
        { value: 'Jewellery', label: 'Jewellery' },
        { value: 'Accessories', label: 'Accessories' },
        { value: 'Beauty', label: 'Beauty' },
        { value: 'Lifestyle', label: 'Lifestyle' },
      ],
      dateRangeOptions: [
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'lastSevenDays', label: 'Last 7 Days' },
        { value: 'thisWeek', label: 'This Week' },
        { value: 'lastWeek', label: 'Last Week' },
        { value: 'thisMonth', label: 'This Month' },
        { value: 'lastMonth', label: 'Last Month' },
        { value: 'lastThirtyDays', label: 'Last 30 Days' },
        { value: 'custom', label: 'Custom' },
      ],
      customDateRange: [],
      isLoading: false,
      isLoadingShopperList: false,
      isLoadingStoreList: false,
      list: [],
      report_type: { value: 'sales', label: 'Customer Sales Report' },
    }
  },
  async beforeRouteEnter(to, from, next) {
    let response = await PrivateClientsService.getShopperList()
    const shopperOptions = response.data

    response = await PrivateClientsService.getStoreList()
    const storeOptions = response.data

    next((vm) => {
      /* eslint-disable-next-line */
      vm._data.shopperOptions = shopperOptions
      vm._data.storeOptions = storeOptions
    })
  },
  methods: {
    getDefaultFilter() {
      return {
        order_status: null,
        shopper: null,
        store: null,
        brand_name: null,
        category: null,
        dateRange: { value: 'yesterday', label: 'Yesterday' },
      }
    },
    getSalesReportPayload() {
      return {
        order_status: this.filter.order_status ? this.filter.order_status.value : null,
        admin_id: this.filter.shopper ? this.filter.shopper.id : null,
        store_id: this.filter.store ? this.filter.store.id : null,
        brand_name: this.filter.brand_name,
        category: this.filter.category ? this.filter.category.value : null,
        start_date: (this.customDateRange[0]) ? format(this.customDateRange[0], 'yyyy-MM-dd HH:mm:ss') : null,
        end_date: (this.customDateRange[1]) ? format(this.customDateRange[1], 'yyyy-MM-dd HH:mm:ss') : null,
        ts: Date.now()
      }
    },
    getMerchantSalesReportPayload() {
      return {
        order_status: this.filter.order_status ? this.filter.order_status.value : null,
        store_id: this.filter.store ? this.filter.store.id : null,
        start_date: (this.customDateRange[0]) ? format(this.customDateRange[0], 'yyyy-MM-dd HH:mm:ss') : null,
        end_date: (this.customDateRange[1]) ? format(this.customDateRange[1], 'yyyy-MM-dd HH:mm:ss') : null,
        ts: Date.now()
      }
    },
    async getReport() {
      this.isLoading = true

      if (this.report_type.value === 'sales') {
        const payload = this.getSalesReportPayload()

        try {
          const response = await PrivateClientsService.getSalesReport(payload)
          // const { data } = response

          this.list = response.data
        } catch (error) {
          this.list = []
        } finally {
          this.isLoading = false
        }
      } else if (this.report_type.value === 'merchant-sales') {
        const payload = this.getMerchantSalesReportPayload()

        try {
          const response = await PrivateClientsService.getMerchantSalesReport(payload)
          // const { data } = response

          this.list = response.data
        } catch (error) {
          this.list = []
        } finally {
          this.isLoading = false
        }
      }
    },
    handleSearch() {
      this.getReport()
    },
    didSelectDateRange(dateRange) {
      this.customDateRange = dateRange
    },
    didUpdateReportType() {
      this.list = []
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

.filter-container {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  flex-grow: 1;
  .filter-title {
    font-size: 13px;
    margin-right: 5px;
  }
}

section {
  .filter-item {
    margin: 3px 0;
    width: 100%;
    @include mobile {
      margin: 3px 5px;
      width: calc(100% / 3 - 10px);
    }
    @include tablet {
      width: calc(100% / 4 - 10px);
    }
    @include desktop {
      width: calc(100% / 6 - 10px);
    }
  }

  .data-picker-container {
    position: relative;
    // Make the hack button span the whole parent container.
    .absolute-view {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }
  }
}
</style>

